import { AxleMenu } from '@/components/AxleMenu';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { CancelVisitDialog } from '@/components/visits/dialogs/CancelVisit/CancelVisitDialog';
import { useVisitActionsMenu } from '@/components/visits/tables/VisitActionsCell';
import { Discipline } from '@/types/graphql';
import { Close } from '@mui/icons-material';
import { Box, IconButton, Link, Popover, Typography } from '@mui/material';
import moment from 'moment';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DateHeaderProps, EventWrapperProps } from 'react-big-calendar';

import { EventStatus, getStatusIcon, readableEventStatus } from './status';
import { CalendarDayMarker, CalendarTile, EngineCalendarEvent } from './types';
import { formatTime } from './utils';

export const DisciplineColorContext = createContext<Map<string, string>>(new Map());
export const DayMarkerContext = createContext<Map<string, CalendarDayMarker[]>>(new Map());

const useDisciplineColor = (code: string | undefined): string => {
    const map = useContext(DisciplineColorContext);
    if (!code) {
        return '#D9D9D9';
    }
    return map.get(code) ?? '#D9D9D9';
};

export function OverflowLink({
    onClick,
    hiddenCount,
}: {
    onClick: (anchorEl: HTMLElement) => void;
    hiddenCount: number;
}) {
    return (
        <Link
            component='button'
            onClick={(e) => onClick(e.currentTarget)}
            sx={{
                color: '#359b9b',
                fontSize: '11px',
                textDecoration: 'underline',
            }}
        >
            + {hiddenCount} More
        </Link>
    );
}

interface DayEventsPopoverProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    events: CalendarTile[];
}

export function DayEventsPopover({ anchorEl, open, onClose, events }: DayEventsPopoverProps) {
    const sortedEvents = [...events].sort((a, b) => {
        const timeA = a.start.getTime();
        const timeB = b.start.getTime();
        return timeA - timeB;
    });

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <Box sx={{ p: 2, minWidth: '150px', maxWidth: '200px' }}>
                <IconButton
                    aria-label={'Close'}
                    onClick={onClose}
                    size='small'
                    sx={{
                        padding: '2px',
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        border: '1px solid #000000',
                        color: '#000000',
                    }}
                >
                    <Close sx={{ fontSize: '12px' }} />
                </IconButton>
                <VerticalStack spacing={1} alignItems='center' mb={1.5}>
                    <Typography variant='body2'>
                        {moment(sortedEvents[0].start).format('dddd')}
                    </Typography>
                    <Box
                        bgcolor='#007A75'
                        color='#FFFFFF'
                        height='28px'
                        width='28px'
                        borderRadius='50%'
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                    >
                        {moment(sortedEvents[0].start).format('D')}
                    </Box>
                </VerticalStack>
                <VerticalStack spacing={0.5}>
                    {sortedEvents.map((event) => (
                        <Box key={event.uuid}>
                            <VisitEventWrapper event={event} />
                        </Box>
                    ))}
                </VerticalStack>
            </Box>
        </Popover>
    );
}

// Renders the shared visit actions menu (same items as the table three-dot menu, via
// useVisitActionsMenu) anchored to a calendar tile, plus the nested CancelVisitDialog.
function CalendarVisitActionsMenu({
    visitId,
    isOpen,
    anchorEl,
    onClose,
}: {
    visitId: string;
    isOpen: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
}) {
    const { items, isCancelDialogOpen, onCloseCancelDialog } = useVisitActionsMenu({
        visitId,
        onActionSelected: onClose,
    });
    return (
        <>
            <AxleMenu
                isOpen={isOpen}
                close={onClose}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClick={(event) => event.stopPropagation()}
                items={items}
            />
            <CancelVisitDialog
                isOpen={isCancelDialogOpen}
                onClose={onCloseCancelDialog}
                visitId={visitId}
            />
        </>
    );
}

export function VisitEventWrapper({ event }: { event: CalendarTile }) {
    const { appointment, status, disciplines } = event;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const isHovered = Boolean(anchorEl);

    // Clicking a visit opens the same actions menu as the visits table three-dot menu,
    // anchored to the calendar tile (PRD-672 / FE-725), instead of routing to the visit
    // details page.
    const tileRef = useRef<HTMLDivElement | null>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // Mount the menu (and fire its lazy query) only after the first open, then keep it
    // mounted so the nested CancelVisitDialog survives closing the menu.
    const [isMenuMounted, setIsMenuMounted] = useState(false);
    const openMenu = () => {
        // Keep the hover preview open (or show it, if they clicked without hovering first),
        // anchored to the tile, so a click surfaces the preview alongside the actions menu.
        setAnchorEl(tileRef.current);
        setIsMenuOpen(true);
        setIsMenuMounted(true);
    };

    // The actions menu hangs off the bottom-right corner of the hover preview, so anchor it
    // to the preview's paper once that mounts (falling back to the tile until then). Stored
    // in state via a stable callback ref so the menu repositions when the preview appears.
    const [previewPaperEl, setPreviewPaperEl] = useState<HTMLDivElement | null>(null);
    const setPreviewPaperRef = useCallback(
        (el: HTMLDivElement | null) => setPreviewPaperEl(el),
        [],
    );

    const disciplineCodes = disciplines.map((d) => d.code).join(', ');
    const disciplineColor = useDisciplineColor(
        disciplines.length > 0 ? disciplines[0].code : undefined,
    );

    const startTime = formatTime(appointment.startDatetime, event.timezone) || null;

    const clinicianName = appointment.clinician
        ? `${appointment.clinician.lastName}, ${appointment.clinician.firstName}`
        : 'Needs Assignment';

    const disciplineLabel = disciplineCodes ? `(${disciplineCodes})` : '';
    const serviceCode = appointment.services.map((s) => s.name).join(', ');

    const popoverBody = (
        <VerticalStack spacing={0.5}>
            <Typography sx={{ fontWeight: '400', color: '#000000', fontSize: '12px' }}>
                {startTime && `${startTime} `}
                <Box component='span' sx={{ fontWeight: '600' }}>
                    {[clinicianName, disciplineLabel].filter(Boolean).join(' ')}
                </Box>
            </Typography>
            {serviceCode && (
                <Typography
                    variant='body2'
                    sx={{ color: '#000000', fontSize: '12px', fontWeight: 400 }}
                >
                    {serviceCode}
                </Typography>
            )}
            <Typography sx={{ fontWeight: '400', color: '#000000', fontSize: '12px' }}>
                {readableEventStatus(status)}
            </Typography>
        </VerticalStack>
    );

    return (
        <>
            <Box
                ref={tileRef}
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => {
                    // Keep the preview up while the actions menu is open.
                    if (!isMenuOpen) {
                        setAnchorEl(null);
                    }
                }}
                onClick={openMenu}
                bgcolor={disciplineColor}
                p='6px 8px'
                borderRadius='4px'
                width='120px'
                sx={{ cursor: 'pointer' }}
            >
                <VerticalStack spacing={0.5}>
                    <HorizontalStack spacing={0.5} alignItems='center' sx={{ overflow: 'hidden' }}>
                        {getStatusIcon(status)}
                        <Typography
                            variant='body2'
                            noWrap
                            sx={{ fontWeight: '400', color: '#000000', fontSize: '10px' }}
                        >
                            {appointment.clinician
                                ? `${disciplineCodes} | ${appointment.clinician.firstName} ${appointment.clinician.lastName}`
                                : disciplineCodes}
                        </Typography>
                    </HorizontalStack>
                    {serviceCode && (
                        <Typography
                            variant='body2'
                            noWrap
                            sx={{ fontWeight: '400', color: '#000000', fontSize: '10px' }}
                        >
                            {serviceCode}
                        </Typography>
                    )}
                </VerticalStack>
            </Box>
            <Popover
                open={isHovered}
                anchorEl={anchorEl}
                onClose={() => {
                    if (!isMenuOpen) {
                        setAnchorEl(null);
                    }
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableRestoreFocus
                sx={{ pointerEvents: 'none' }}
                slotProps={{
                    paper: {
                        ref: setPreviewPaperRef,
                        onMouseEnter: (e) => setAnchorEl(e.currentTarget),
                        onMouseLeave: () => {
                            if (!isMenuOpen) {
                                setAnchorEl(null);
                            }
                        },
                        sx: {
                            pointerEvents: 'auto',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        },
                    },
                }}
            >
                <Box
                    onClick={openMenu}
                    bgcolor={disciplineColor}
                    p='8px 12px'
                    borderRadius='4px'
                    minWidth='180px'
                    sx={{ cursor: 'pointer' }}
                >
                    {popoverBody}
                </Box>
            </Popover>
            {isMenuMounted && (
                <CalendarVisitActionsMenu
                    visitId={event.id}
                    isOpen={isMenuOpen}
                    anchorEl={previewPaperEl ?? tileRef.current}
                    onClose={() => {
                        setIsMenuOpen(false);
                        // Closing the menu also dismisses the preview it was hanging off of.
                        setAnchorEl(null);
                    }}
                />
            )}
        </>
    );
}

/**
 * Generic all-day labeled span: a coloured pill label followed by a matching connector line.
 * Colour and label are caller-supplied — the shared engine attaches no domain meaning.
 */
export function SpanBar({ label, color }: { label: string; color: string }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '28px', width: '100%', pl: 1 }}>
            <Box
                sx={{
                    bgcolor: color,
                    color: '#FFFFFF',
                    borderRadius: '4px',
                    px: 1,
                    py: 0.25,
                    fontSize: '11px',
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                {label}
            </Box>
            <Box
                sx={{
                    flex: 1,
                    height: '2px',
                    bgcolor: color,
                    mr: '10px',
                }}
            />
        </Box>
    );
}

/**
 * Generic day-number-header badge. `start`/`end` shapes clip a chevron pointing into the
 * span's interior; `plain` renders a flat badge. Colour and label are caller-supplied.
 */
export function DayMarkerBadge({
    label,
    color,
    shape,
}: {
    label: string;
    color: string;
    shape?: 'start' | 'end' | 'plain';
}) {
    return (
        <Box
            sx={{
                width: '100%',
                height: '20px',
                bgcolor: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
                fontSize: '10px',
                fontWeight: 400,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                ...(shape === 'start'
                    ? {
                          clipPath:
                              'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)',
                      }
                    : shape === 'end'
                    ? {
                          clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 8px 100%, 0 50%)',
                      }
                    : {}),
            }}
        >
            {label}
        </Box>
    );
}

export const CustomEventWrapper = ({ event }: EventWrapperProps<EngineCalendarEvent>) => {
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

    if (event.kind === 'tile') {
        return <VisitEventWrapper event={event} />;
    }

    if (event.kind === 'bar') {
        return <SpanBar label={event.label} color={event.color} />;
    }

    return (
        <>
            <OverflowLink
                onClick={(anchorEl) => setPopoverAnchor(anchorEl)}
                hiddenCount={event.hiddenCount}
            />
            <DayEventsPopover
                anchorEl={popoverAnchor}
                open={!!popoverAnchor}
                onClose={() => setPopoverAnchor(null)}
                events={event.dayEvents}
            />
        </>
    );
};

const DAY_MARKER_LABEL_THRESHOLD = 120;

export const CustomMonthHeader = ({ date }: DateHeaderProps) => {
    const dayMarkersByDay = useContext(DayMarkerContext);
    const dateMoment = moment(date);
    const dayMarkers = dayMarkersByDay.get(moment(date).format('YYYY-MM-DD')) ?? [];
    const containerRef = useRef<HTMLDivElement>(null);
    const [isNarrow, setIsNarrow] = useState(false);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            setIsNarrow(entry.contentRect.width < DAY_MARKER_LABEL_THRESHOLD);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            ref={containerRef}
            display='flex'
            alignItems='flex-start'
            gap={0.5}
            color='#3C4043'
            width='100%'
        >
            <Box fontWeight='400' fontSize='14px' flexShrink={0}>
                {dateMoment.format('D')}
            </Box>
            <Box display='flex' flexDirection='column' gap={0.5} flex={1} minWidth={0}>
                {dayMarkers.map((marker) => (
                    <DayMarkerBadge
                        key={marker.id}
                        label={isNarrow && marker.shortLabel ? marker.shortLabel : marker.label}
                        color={marker.color}
                        shape={marker.shape}
                    />
                ))}
            </Box>
        </Box>
    );
};

export const StatusLegend = () => {
    const statuses = Object.values(EventStatus);

    return (
        <VerticalStack spacing={1}>
            <Typography variant='caption' fontWeight={600} borderBottom={'2px solid'}>
                Status
            </Typography>
            <Box display='flex' flexWrap='nowrap' gap={2}>
                {statuses.map((status) => (
                    <HorizontalStack
                        key={status}
                        alignItems='center'
                        gap='5px'
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {getStatusIcon(status)}
                        <Box fontSize={12}>{readableEventStatus(status)}</Box>
                    </HorizontalStack>
                ))}
            </Box>
        </VerticalStack>
    );
};

export const DisciplineLegend = ({
    disciplines,
}: {
    disciplines: Pick<Discipline, 'id' | 'code'>[];
}) => {
    const colorMap = useContext(DisciplineColorContext);

    return (
        <VerticalStack spacing={1} overflow={'auto'}>
            <Typography variant='caption' fontWeight={600} borderBottom={'2px solid'}>
                Discipline
            </Typography>
            <Box display='flex' flexWrap='nowrap' gap={2}>
                {disciplines.map((discipline) => (
                    <HorizontalStack
                        key={discipline.id}
                        alignItems='center'
                        gap='5px'
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        <Box
                            width='12px'
                            height='12px'
                            bgcolor={colorMap.get(discipline.code) ?? '#D9D9D9'}
                            borderRadius='2px'
                            flexShrink={0}
                        />
                        <Box fontSize={12}>{discipline.code}</Box>
                    </HorizontalStack>
                ))}
            </Box>
        </VerticalStack>
    );
};
